import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function ExpenseReport() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, companyName, branchId, permissions, token, logout } = useAuth();
    const [reportGeneratedTime, setReportGeneratedTime] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchExpense();

    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    // Expense
    const [ExpenseOptions, setExpenseOptions] = useState([]);
    const [selectedExpense, setSelectedExpense] = useState("");
    const fetchExpense = async () => {
        try {
            const res = await fetchData('InvoiceApi', `/IncomeExpense/${companyId}`, token);

            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({
                    value: c.Id,
                    label: c.Name
                }));
                setExpenseOptions(options);
            }
        } catch (e) {
            console.error('Error fetching Expense:', e);
        }
    };

    const handleExpenseChange = async (opt) => {
        setSelectedExpense(opt);
    };

    const fetchTableData = async () => {
        const now = new Date();
        const formattedDateTime =
            String(now.getDate()).padStart(2, '0') + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            now.getFullYear() + ' ' +
            now.toLocaleTimeString(); // e.g., 10:45:23 AM

        setReportGeneratedTime(formattedDateTime);
        setDataItem([]);
        setLoading(true);
        try {
            // if selectedExpense exists, take value, else use 0
            const expenseId = selectedExpense?.value ?? 0;

            const response = await fetchData(
                'ReportApi',
                `/Expense/${fromDate}/${toDate}/${companyId}/${expenseId}/${companyId}`,
                token
            );

            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
                setSelectedExpense('');
            }
        } catch (error) {
            console.error('Error fetching Expense Details:', error);
        } finally {
            setLoading(false); // ✅ Hide loading screen always
        }
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    // Search data
    const [isFocused, setIsFocused] = useState(false);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);;
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${day}-${month}-${year}`;

        return formattedDate;
    };

    const handleExportExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Expense Report");
            const lastColumn = 10;

            // Freeze header rows
            worksheet.views = [{ state: "frozen", ySplit: 2 }];

            // Title row
            worksheet.addRow(["Expense Report"]);
            worksheet.mergeCells("A1:J1");
            const titleCell = worksheet.getCell("A1");
            titleCell.font = { size: 14, bold: true };
            titleCell.alignment = { horizontal: "center" };

            // Report generated time
            if (reportGeneratedTime) {
                worksheet.addRow([`Report Generated: ${reportGeneratedTime}`]);
                worksheet.mergeCells("A2:J2");
                worksheet.getCell("A2").alignment = { horizontal: "right" };
                worksheet.getCell("A2").font = { italic: true, size: 10 };
            }

            worksheet.addRow([]);

            // Table Header
            const header = [
                "Invoice Date",
                "Invoice No",
                "BL No",
                "Customer",
                "Consignee",
                "Remarks",
                "Amount",
                "Vat Rate",
                "Vat Amount",
                "Total",
            ];
            const headerRow = worksheet.addRow(header);
            headerRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF102E50" } };
                // Right align numeric headers
                cell.alignment = { horizontal: colNumber >= 7 ? "right" : "center" };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });

            // 👉 Set fixed column widths
            worksheet.columns = [
                { width: 12 }, // Invoice Date
                { width: 12 }, // Invoice No
                { width: 12 }, // BL No
                { width: 18 }, // Customer
                { width: 18 }, // Consignee                
                { width: 22 }, // Remarks
                { width: 12 }, // Amount
                { width: 10 }, // Vat Rate
                { width: 12 }, // Vat Amount
                { width: 12 }, // Total
            ];

            // Grouping by HeadName
            const groupedData = filteredDataItem.reduce((acc, item) => {
                if (!acc[item.HeadName]) acc[item.HeadName] = [];
                acc[item.HeadName].push(item);
                return acc;
            }, {});

            // Fill rows group by group
            Object.entries(groupedData).forEach(([headName, items]) => {
                // Group header row
                worksheet.addRow([headName]);
                worksheet.mergeCells(`A${worksheet.lastRow.number}:J${worksheet.lastRow.number}`);
                const groupHeader = worksheet.getRow(worksheet.lastRow.number);
                groupHeader.font = { bold: true, size: 12 };

                // Apply fill only to merged cell (A..J)
                const headerCell = groupHeader.getCell(1);
                headerCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFCECBCB" },
                };

                // Data rows
                items.forEach((data) => {
                    const dataRow = worksheet.addRow([
                        formatDate(data.InvoiceDate),
                        data.InvoiceNumber,
                        data.BLNumber,
                        data.CustomerName,
                        data.ConsigneeName,
                        data.Remarks,
                        data.Amount?.toFixed(3) ?? "--",
                        data.VatRate?.toFixed(3) ?? "--",
                        data.VatAmount?.toFixed(3) ?? "--",
                        data.TotalAmount?.toFixed(3) ?? "--",
                    ]);

                    // ✅ Align numeric columns right
                    for (let i = 7; i <= 10; i++) {
                        dataRow.getCell(i).alignment = { horizontal: "right" };
                    }
                });

                // Group Totals
                const groupAmount = items.reduce((sum, i) => sum + (i.Amount || 0), 0);
                const groupVat = items.reduce((sum, i) => sum + (i.VatAmount || 0), 0);
                const groupTotal = items.reduce((sum, i) => sum + (i.TotalAmount || 0), 0);

                const groupTotalRow = worksheet.addRow([
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    groupAmount.toFixed(3),
                    "",
                    groupVat.toFixed(3),
                    groupTotal.toFixed(3),
                ]);
                groupTotalRow.font = { bold: true };

                // ✅ Apply fill only to A..J
                for (let i = 1; i <= lastColumn; i++) {
                    const cell = groupTotalRow.getCell(i);
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFCCFFCC" }, // light green
                    };
                }

                // ✅ Right align numeric totals
                for (let i = 7; i <= 10; i++) {
                    groupTotalRow.getCell(i).alignment = { horizontal: "right" };
                }
            });

            // Grand Total
            const grandAmount = filteredDataItem.reduce((sum, i) => sum + (i.Amount || 0), 0);
            const grandVat = filteredDataItem.reduce((sum, i) => sum + (i.VatAmount || 0), 0);
            const grandTotal = filteredDataItem.reduce((sum, i) => sum + (i.TotalAmount || 0), 0);

            const grandTotalRow = worksheet.addRow([
                "",
                "",
                "",
                "",
                "",
                "Grand Total",
                grandAmount.toFixed(3),
                "",
                grandVat.toFixed(3),
                grandTotal.toFixed(3),
            ]);
            grandTotalRow.font = { bold: true };

            // ✅ Apply fill only to A..J
            for (let i = 1; i <= lastColumn; i++) {
                const cell = grandTotalRow.getCell(i);
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFDCDCDC" }, // grey
                };
            }

            // ✅ Right align numeric totals
            for (let i = 7; i <= 10; i++) {
                grandTotalRow.getCell(i).alignment = { horizontal: "right" };
            }

            // Save file
            const buf = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buf]), `ExpenseReport_${new Date().toISOString().split("T")[0]}.xlsx`);
        } catch (err) {
            console.error("Error exporting Excel:", err);
        }
    };

    return (
        <div
            className="w-full mx-auto"
            style={{
                width: "100%",
                marginTop: "0",
                background: "#fff",
                border: "1px solid #dcdcdc",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
        >
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="loader"></div>
                    <span style={{ color: 'white', marginTop: '10px', fontSize: '14px' }}>
                        Loading...
                    </span>
                </div>
            )}
            {/* ===== Report Header ===== */}
            <div
                style={{
                    background: "whitesmoke",
                    color: "black",
                    padding: "5px 10px",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    marginTop: '-30px',
                    height: '30px',
                    textTransform: 'uppercase'
                }}
            >
                Expense Report
            </div>

            {/* ===== Filter Section ===== */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between", // ✅ left + right alignment
                    padding: "10px 15px",
                    borderBottom: "1px solid #eee",
                    background: "#fafafa",
                }}
            >
                {/* ===== Left: Search + Timestamp ===== */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? "260px" : "200px",
                            height: "30px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            padding: "0 10px",
                            transition: "width 0.3s",
                            outline: "none",
                            fontSize: "13px",
                        }}
                    />

                    {reportGeneratedTime && (
                        <div
                            style={{
                                fontSize: "11px",
                                color: "red",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Report Generated: {reportGeneratedTime}
                        </div>
                    )}
                </div>

                {/* ===== Right: Dates, Dropdown, Buttons ===== */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* From Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>From</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>To</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* Expense Head */}
                    <div style={{ marginTop: "-10px" }}>
                        <label
                            className="block uppercase text-black text-xs mb-1"
                            style={{
                                fontFamily: "monospace",
                                fontSize: "12.5px",
                                marginTop: "-10px",
                            }}
                        >
                            Expense Head
                        </label>
                        <Select
                            value={selectedExpense}
                            onChange={handleExpenseChange}
                            options={ExpenseOptions}
                            placeholder="Expense"
                            isSearchable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: "30px",
                                    height: "30px",
                                    fontSize: "14px",
                                    minWidth: "250px",
                                    width: "250px",
                                }),
                                valueContainer: (base) => ({
                                    ...base,
                                    height: "30px",
                                    padding: "0 6px",
                                    width: "250px",
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    height: "30px",
                                    width: "250px",
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 1301,
                                    fontSize: "14px",
                                }),
                            }}
                        />
                    </div>

                    {/* Refresh */}
                    <Button
                        onClick={fetchTableData}
                        style={{
                            background: "#2f4050",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Refresh
                    </Button>

                    {/* Excel */}
                    <Button
                        onClick={handleExportExcel}
                        style={{
                            background: "#00695c",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Excel
                    </Button>
                </div>
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '570px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#102E50' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '5%' }}>Invoice Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '5%' }}>Invoice No</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '5%' }}>BL No</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '10%' }}>Customer</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '10%' }}>Consignee</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', padding: '3px', width: '10%' }}>Remarks</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'right', padding: '3px', width: '5%' }}>Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'right', padding: '3px', width: '5%' }}>Vat Rate</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'right', padding: '3px', width: '5%' }}>Vat Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', fontSize: '10px', color: 'whitesmoke', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'right', padding: '3px', width: '5%' }}>Total</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.entries(
                                filteredDataItem.reduce((acc, item) => {
                                    if (!acc[item.HeadName]) acc[item.HeadName] = [];
                                    acc[item.HeadName].push(item);
                                    return acc;
                                }, {})
                            ).map(([headName, items]) => {
                                // Group totals
                                const groupAmount = items.reduce((sum, i) => sum + (i.Amount || 0), 0);
                                const groupVat = items.reduce((sum, i) => sum + (i.VatAmount || 0), 0);
                                const groupTotal = items.reduce((sum, i) => sum + (i.TotalAmount || 0), 0);

                                return (
                                    <React.Fragment key={headName}>
                                        {/* Group Header Row */}
                                        <TableRow>
                                            <TableCell
                                                colSpan={11}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    padding: '6px',
                                                    textAlign: 'left',
                                                    backgroundColor: '#cecbcbff',
                                                    color: 'black',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {headName}
                                            </TableCell>
                                        </TableRow>

                                        {/* Grouped Details */}
                                        {items.map((data) => (
                                            <TableRow key={data.id}>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{formatDate(data.InvoiceDate)}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{data.InvoiceNumber}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{data.BLNumber}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{data.CustomerName}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{data.ConsigneeName}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', fontSize: '11px' }}>{data.Remarks}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                                    {data.Amount?.toFixed(3) ?? "--"}
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                                    {data.VatRate ?? "--"}
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                                    {data.VatAmount?.toFixed(3) ?? "--"}
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #f4dfdfff', padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                                    {data.TotalAmount?.toFixed(3) ?? "--"}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* Group Total Row */}
                                        <TableRow style={{ backgroundColor: '#d6f8f173' }}>
                                            <TableCell colSpan={6} sx={{ fontWeight: 'bold', textAlign: 'right', padding: '6px' }}>

                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                                {groupAmount.toFixed(3) ?? "--"}
                                            </TableCell>
                                            <TableCell /> {/* Empty for VatRate */}
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                                {groupVat.toFixed(3) ?? "--"}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                                {groupTotal.toFixed(3) ?? "--"}
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}

                            {/* Grand Total Row */}
                            <TableRow sx={{ backgroundColor: '#dcdcdc' }}>
                                <TableCell colSpan={6} sx={{ fontWeight: 'bold', textAlign: 'right', padding: '6px' }}>
                                    Grand Total :
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                    {filteredDataItem.reduce((sum, i) => sum + (i.Amount || 0), 0).toFixed(3)}
                                </TableCell>
                                <TableCell />
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                    {filteredDataItem.reduce((sum, i) => sum + (i.VatAmount || 0), 0).toFixed(3)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '6px', fontSize: '12px' }}>
                                    {filteredDataItem.reduce((sum, i) => sum + (i.TotalAmount || 0), 0).toFixed(3)}
                                </TableCell>
                            </TableRow>
                        </TableBody>

                    </Table>
                </TableContainer>
            </div>

        </div>
    );
}

export default ExpenseReport;
